import { useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider, IconButton,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { accountsApi, journalsWriteApi, type AccountDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { todayIso } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

interface Line {
  key: string; account: AccountDto | null;
  debit: number; credit: number; description: string;
}
const blankLine = (): Line => ({
  key: crypto.randomUUID(), account: null, debit: 0, credit: 0, description: "",
});

/**
 * UC-M1-ACC-01 — manual journal. Two-line minimum, debit = credit within 0.01.
 * Posting writes a balanced JournalEntry via the spec's state machine; blocked when
 * the target period (by entryDate) is closed.
 */
export function ManualJournalPage() {
  const navigate = useNavigate();
  const [entryDate, setEntryDate] = useState(todayIso);
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<Line[]>([blankLine(), blankLine()]);

  const accounts = useQuery({
    queryKey: ["accounts-all"],
    queryFn: () => accountsApi.list({ isActive: true, pageSize: 500 }),
    staleTime: 60_000,
  });

  const totalDebit  = lines.reduce((s, l) => s + (l.debit  || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (l.credit || 0), 0);
  const delta = totalDebit - totalCredit;
  const isBalanced = Math.abs(delta) <= 0.01;

  const update = (key: string, patch: Partial<Line>) =>
    setLines(ls => ls.map(l => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key: string) => setLines(ls => ls.filter(l => l.key !== key));

  const validationError =
    !isBalanced ? "Debit must equal credit within 0.01." :
    lines.length < 2 ? "A journal entry needs at least two lines." :
    lines.some(l => !l.account) ? "Pick an account on every line." :
    lines.some(l => (l.debit > 0 && l.credit > 0) || (l.debit === 0 && l.credit === 0))
      ? "Each line must have exactly one of debit or credit (> 0)." :
    null;

  const mutation = useMutation({
    meta: { successMessage: "Journal entry posted" },
    mutationFn: async () => {
      const created = await journalsWriteApi.create({
        entryDate, description: description || undefined,
        lines: lines.map(l => ({
          accountId: l.account!.id, debit: l.debit, credit: l.credit,
          description: l.description || undefined,
        })),
      });
      return created.id;
    },
    onSuccess: (id) => navigate(`/accounting/journals/${id}`),
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New manual journal</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          <TextField label="Entry date *" type="date" size="small"
            InputLabelProps={{ shrink: true }}
            value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
          <TextField label="Description" size="small" sx={{ flex: 1, minWidth: 280 }}
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </Stack>

        <Divider sx={{ mb: 2 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 300 }}>Account *</TableCell>
              <TableCell sx={{ minWidth: 240 }}>Description</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Debit (₹)</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Credit (₹)</TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l) => (
              <TableRow key={l.key}>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={accounts.data?.items ?? []}
                    getOptionLabel={(o) => `${o.code} — ${o.name}`}
                    value={l.account}
                    onChange={(_, v) => update(l.key, { account: v })}
                    renderInput={(params) => <TextField {...params} placeholder="Pick an account" />}
                    loading={accounts.isLoading}
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth value={l.description}
                    onChange={(e) => update(l.key, { description: e.target.value })} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 130 }}
                    value={l.debit} onChange={(e) => update(l.key, { debit: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 130 }}
                    value={l.credit} onChange={(e) => update(l.key, { credit: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => remove(l.key)} disabled={lines.length <= 2}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button startIcon={<AddIcon />} sx={{ mt: 1 }} size="small"
          onClick={() => setLines(ls => [...ls, blankLine()])}>
          Add line
        </Button>

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={4} alignItems="center">
          <Stack>
            <Typography variant="caption" color="text.secondary">Total debit</Typography>
            <Typography variant="h6">{inr.format(totalDebit)}</Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">Total credit</Typography>
            <Typography variant="h6">{inr.format(totalCredit)}</Typography>
          </Stack>
          <Chip
            color={isBalanced ? "success" : "error"}
            label={isBalanced ? "Balanced" : `Off by ${inr.format(Math.abs(delta))}`}
            sx={{ fontWeight: 600 }}
          />
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
          <Button onClick={() => navigate("/accounting/journals")}>Cancel</Button>
          <Button variant="contained" color="success"
            disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>
            Save draft
          </Button>
        </Stack>
        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
      </Paper>
    </Box>
  );
}
